import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const loc = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const res = await register({
      full_name: form.full_name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim() || undefined,
      password: form.password,
    });
    setLoading(false);
    if (!res.ok) {
      setErr(res.error);
      toast.error(res.error);
      return;
    }
    toast.success("Account created");
    const dest = loc.state?.next || "/dashboard";
    navigate(dest, { replace: true });
  };

  return (
    <PublicLayout>
      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-md px-4 sm:px-6">
          <div className="rounded-2xl border border-[#E5E2DE] bg-white p-8 shadow-sm">
            <div className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#E87722]">Get started</div>
            <h1 className="font-heading text-3xl font-bold text-ink">Create your account</h1>
            <p className="mt-2 text-sm text-ink-soft">It takes less than a minute.</p>
            <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="register-form">
              <div>
                <Label htmlFor="full_name">Full name</Label>
                <Input id="full_name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-1.5" data-testid="register-name" />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="register-email" />
              </div>
              <div>
                <Label htmlFor="phone">Phone (optional)</Label>
                <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" data-testid="register-phone" />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" data-testid="register-password" />
              </div>
              {err && <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="register-error">{err}</div>}
              <Button type="submit" disabled={loading} className="w-full bg-[#5B2C82] text-white hover:bg-[#46225f]" data-testid="register-submit">
                {loading ? "Creating account…" : "Create account"}
              </Button>
            </form>
            <div className="mt-5 text-center text-sm text-ink-soft">
              Already have an account?{" "}
              <Link to="/login" className="font-semibold text-[#5B2C82] hover:text-[#E87722]" data-testid="register-login-link">
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
